import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { CATEGORIES } from "./wbsConfig";
import {
  LEDGER_TYPES,
  buildSampleEntries,
  newEntryId,
  validateEntry,
} from "./ledger";

const FIELD_HINT = {
  type: "a type",
  amount: "an amount",
  date: "a date",
  line: "a line",
};

const cellSx = { px: 1, py: 0.5 };

const toDraft = (entry) => ({ ...entry, amount: String(entry.amount) });

const isBlank = (row) =>
  String(row.amount).trim() === "" && !row.date && !String(row.note).trim();

/**
 * Edits the dated cost entries behind the dashboard's Budget / Actual /
 * Invoiced / Paid figures - one dated amount per row, like the dated invoices
 * in the MIS Invoices tab. Invoices are per category (that is how MIS keeps
 * them); everything else is per WBS line.
 *
 * @param entries  saved entries (normalised, numeric amounts)
 * @param lines    dashboard lines (for the line picker and the sample loader)
 * @param onSave   async (entries) => void; a rejection is shown, not swallowed
 */
export default function LedgerDialog({ open, onClose, entries, lines, onSave }) {
  const [draft, setDraft] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(entries.map(toDraft));
    setShowErrors(false);
    setError("");
  }, [open, entries]);

  const update = (id, patch) =>
    setDraft((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const changeType = (id, type) =>
    update(
      id,
      type === "inv"
        ? { type, code: null, cat: "2" }
        : { type, cat: null, code: lines[0]?.code || "" },
    );

  const addRow = () =>
    setDraft((rows) => [
      ...rows,
      {
        id: newEntryId(),
        type: "act",
        code: lines[0]?.code || "",
        cat: null,
        date: "",
        amount: "",
        note: "",
        source: "manual",
      },
    ]);

  const loadSample = () =>
    setDraft((rows) => [
      ...rows.filter((row) => row.source !== "sample"),
      ...buildSampleEntries(lines).map(toDraft),
    ]);

  const removeSample = () =>
    setDraft((rows) => rows.filter((row) => row.source !== "sample"));

  const sampleCount = draft.filter((row) => row.source === "sample").length;
  // Sample entries are managed as a set (Load / Remove), not row by row - a
  // couple of hundred editable inputs helps nobody. Only your own are listed.
  const listedRows = draft.filter((row) => row.source !== "sample");

  const handleSave = async () => {
    const rows = draft.filter((row) => !isBlank(row));
    const problems = rows.map(validateEntry).filter(Boolean);
    if (problems.length > 0) {
      setShowErrors(true);
      setError(
        `${problems.length} row${problems.length === 1 ? "" : "s"} still need ${[
          ...new Set(problems.map((p) => FIELD_HINT[p])),
        ].join(", ")}.`,
      );
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave(
        rows.map((row) => ({
          ...row,
          amount: Number(row.amount),
          code: row.type === "inv" ? null : row.code,
          cat: row.type === "inv" ? row.cat : null,
          note: String(row.note).trim(),
        })),
      );
      onClose();
    } catch (saveError) {
      console.error("Failed to save 5D entries:", saveError);
      setError("Couldn’t save to the plant MIS record. Nothing was changed.");
    } finally {
      setSaving(false);
    }
  };

  const invalid = (row, field) => showErrors && validateEntry(row) === field;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>Enter cost data</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="info" variant="outlined">
            Saved in this plant’s MIS record — the store the MIS Invoices tab
            uses — and <b>added to</b> what POs, payments and MIS invoices
            already provide, so don’t re-enter those. One dated amount per row;
            invoices are per category, as in MIS.
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRow}>
              Add row
            </Button>
            <Button size="small" variant="outlined" onClick={loadSample}>
              Load sample data
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={sampleCount === 0}
              onClick={removeSample}
            >
              Remove sample data
            </Button>
            <Typography variant="caption" color="text.secondary">
              {listedRows.length} entered
              {sampleCount ? ` · ${sampleCount} sample (not listed)` : ""}
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Type", "WBS line / category", "Date", "Amount ($)", "Note", ""].map(
                    (head) => (
                      <TableCell key={head} sx={cellSx}>
                        {head}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {listedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {sampleCount > 0
                          ? "Sample data is loaded (not listed here). Add a row for your own entries."
                          : "No entries yet. Add a row, or load sample data."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {listedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ ...cellSx, width: 130 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.type}
                        onChange={(e) => changeType(row.id, e.target.value)}
                        inputProps={{ "aria-label": "Type" }}
                      >
                        {LEDGER_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 240 }}>
                      {row.type === "inv" ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.cat || ""}
                          error={invalid(row, "line")}
                          onChange={(e) => update(row.id, { cat: e.target.value })}
                          inputProps={{ "aria-label": "Category" }}
                        >
                          {CATEGORIES.map((cat) => (
                            <MenuItem key={cat.k} value={cat.k}>
                              {cat.code} · {cat.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={row.code || ""}
                          error={invalid(row, "line")}
                          onChange={(e) => update(row.id, { code: e.target.value })}
                          inputProps={{ "aria-label": "WBS line" }}
                        >
                          {lines.map((line) => (
                            <MenuItem key={line.code} value={line.code}>
                              {line.displayCode} · {line.desc}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 160 }}>
                      <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={row.date}
                        error={invalid(row, "date")}
                        onChange={(e) => update(row.id, { date: e.target.value })}
                        inputProps={{ "aria-label": "Date" }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 140 }}>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={row.amount}
                        error={invalid(row, "amount")}
                        onChange={(e) => update(row.id, { amount: e.target.value })}
                        inputProps={{ "aria-label": "Amount", step: "any" }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.note}
                        onChange={(e) => update(row.id, { note: e.target.value })}
                        inputProps={{ "aria-label": "Note" }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 48 }}>
                      <IconButton
                        size="small"
                        aria-label="Delete row"
                        onClick={() =>
                          setDraft((rows) => rows.filter((r) => r.id !== row.id))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
